/**
 * MonitorsPage — Operational Health Layer for the 13-Week Cashflow Forecast.
 *
 * Modeled after the Executive Command Center pattern: top-level health scores,
 * tabbed drill-downs, and a unified alert timeline.
 */

import React, { useMemo, useState } from 'react';
import {
  Panel,
  PanelHeader,
  PanelTitle,
  PanelBody,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Badge,
  Button,
  Spinner,
  Alert,
} from '../../components/ui';
import { MetricCard, MetricCardGroup } from '../../components/ui/metric-card';
import { cn } from '../../shared/lib/cn';
import {
  ChartBarIcon,
  ServerStackIcon,
  ArrowTrendingDownIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import {
  useMonitorSummary,
  useMonitorAlerts,
  useUpdateAlert,
  useMonitorRuns,
  useTriggerMonitorRun,
  useAccuracyData,
  useDriftSummary,
  useDataQualityStatus,
} from './api';
import type {
  MonitorSummary,
  MonitorAlert,
  MonitorGroup,
  MonitorGroupHealth,
  AlertSeverity,
} from './types';

// ── Constants ────────────────────────────────────────────────────────

const GROUP_META: Record<MonitorGroup, { label: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>>; description: string }> = {
  forecast_accuracy: {
    label: 'Forecast Accuracy',
    icon: ChartBarIcon,
    description: 'MAPE rolling trends, line-item variance tracking',
  },
  data_quality: {
    label: 'Data Quality',
    icon: ServerStackIcon,
    description: 'Feed freshness, reconciliation, entity coverage',
  },
  model_drift: {
    label: 'Model Drift',
    icon: ArrowTrendingDownIcon,
    description: 'Behavior class timing, recovery rate deviations',
  },
  agent_health: {
    label: 'Agent Health',
    icon: CpuChipIcon,
    description: 'Run success rates, latency, error frequency',
  },
};

const SEVERITY_STYLES: Record<AlertSeverity, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800/40',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800/40',
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800/40',
};

const SEVERITY_DOT: Record<AlertSeverity, string> = {
  critical: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-blue-500',
};

const SCORE_COLOR = (score: number) => {
  if (score >= 90) return 'text-emerald-600 dark:text-emerald-400';
  if (score >= 70) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
};

const STATUS_COLOR = (status: string) => {
  if (status === 'healthy') return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
  if (status === 'warning') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
};

// ── Main Component ───────────────────────────────────────────────────

export default function MonitorsPage() {
  const { data: summary, isLoading: summaryLoading } = useMonitorSummary();
  const [activeTab, setActiveTab] = useState<string>('overview');

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Health Score Ribbon */}
      {summary && <HealthRibbon summary={summary} />}

      {/* Tabbed Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="forecast_accuracy">
            Forecast Accuracy
            {summary?.groups.forecast_accuracy && summary.groups.forecast_accuracy.total_active > 0 && (
              <Badge variant="danger" className="ml-1.5 text-[10px] px-1.5 py-0">
                {summary.groups.forecast_accuracy.total_active}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="model_drift">
            Model Drift
            {summary?.groups.model_drift && summary.groups.model_drift.total_active > 0 && (
              <Badge variant="danger" className="ml-1.5 text-[10px] px-1.5 py-0">
                {summary.groups.model_drift.total_active}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="data_quality">Data Quality</TabsTrigger>
          <TabsTrigger value="agent_health">Agent Health</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab summary={summary} />
        </TabsContent>
        <TabsContent value="forecast_accuracy">
          <ForecastAccuracyTab />
        </TabsContent>
        <TabsContent value="model_drift">
          <ModelDriftTab />
        </TabsContent>
        <TabsContent value="data_quality">
          <DataQualityTab />
        </TabsContent>
        <TabsContent value="agent_health">
          <AgentHealthTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Score Arc (SVG) ──────────────────────────────────────────────────

function ScoreArc({ score, size = 88, stroke = 6 }: { score: number; size?: number; stroke?: number }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = (score / 100) * circumference;
  const color =
    score >= 90 ? 'stroke-emerald-500' :
    score >= 70 ? 'stroke-amber-500' :
    'stroke-red-500';
  const trackColor = 'stroke-gray-200 dark:stroke-gray-700';

  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
        className={trackColor} strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
        className={color} strokeWidth={stroke}
        strokeDasharray={`${filled} ${circumference - filled}`}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.6s ease' }} />
    </svg>
  );
}

// ── Score Bar ────────────────────────────────────────────────────────

function ScoreBar({ score }: { score: number }) {
  const color =
    score >= 90 ? 'bg-emerald-500' :
    score >= 70 ? 'bg-amber-500' :
    'bg-red-500';
  const trackColor = 'bg-gray-200 dark:bg-gray-700';

  return (
    <div className={cn('h-1 rounded-full w-full', trackColor)}>
      <div
        className={cn('h-1 rounded-full', color)}
        style={{ width: `${Math.min(score, 100)}%`, transition: 'width 0.6s ease' }}
      />
    </div>
  );
}

// ── Health Ribbon ────────────────────────────────────────────────────

function HealthRibbon({ summary }: { summary: MonitorSummary }) {
  const alertTotal = summary.total_critical + summary.total_warning;
  const statusLabel =
    summary.overall_status === 'healthy' ? 'All Systems Nominal' :
    summary.overall_status === 'warning' ? 'Degraded — Review Required' :
    'Critical — Immediate Attention';

  return (
    <div className="space-y-4">
      {/* Command Center Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className={cn(
                'absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping',
                summary.overall_status === 'healthy' ? 'bg-emerald-400' :
                summary.overall_status === 'warning' ? 'bg-amber-400' : 'bg-red-400',
              )} />
              <span className={cn(
                'relative inline-flex rounded-full h-2.5 w-2.5',
                summary.overall_status === 'healthy' ? 'bg-emerald-500' :
                summary.overall_status === 'warning' ? 'bg-amber-500' : 'bg-red-500',
              )} />
            </span>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 tracking-wide uppercase">
              Operational Monitors
            </h2>
          </div>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            Live &middot; Refreshes every 60s
          </span>
        </div>
        {alertTotal > 0 && (
          <div className="flex items-center gap-2 text-xs">
            {summary.total_critical > 0 && (
              <span className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                {summary.total_critical} critical
              </span>
            )}
            {summary.total_warning > 0 && (
              <span className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                {summary.total_warning} warning{summary.total_warning > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Score Cards */}
      <div className="grid grid-cols-5 gap-3">
        {/* Overall System Health — Hero Card */}
        <div className={cn(
          'col-span-1 rounded-lg border p-5 flex flex-col items-center justify-center gap-1',
          'bg-white dark:bg-dark-surface',
          summary.overall_status === 'healthy'
            ? 'border-emerald-200 dark:border-emerald-800/40'
            : summary.overall_status === 'warning'
            ? 'border-amber-200 dark:border-amber-800/40'
            : 'border-red-200 dark:border-red-800/40',
        )}>
          <div className="relative">
            <ScoreArc score={summary.overall_score} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={cn('text-2xl font-bold tabular-nums leading-none', SCORE_COLOR(summary.overall_score))}>
                {summary.overall_score}
              </span>
              <span className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">/100</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <ShieldCheckIcon className={cn(
              'w-3.5 h-3.5',
              summary.overall_status === 'healthy' ? 'text-emerald-500' :
              summary.overall_status === 'warning' ? 'text-amber-500' : 'text-red-500',
            )} />
            <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300 text-center leading-tight">
              {statusLabel}
            </span>
          </div>
        </div>

        {/* Per-Subsystem Cards */}
        {(Object.entries(summary.groups) as [MonitorGroup, MonitorGroupHealth][]).map(
          ([group, health]) => {
            const meta = GROUP_META[group];
            const Icon = meta.icon;
            const hasIssues = health.critical_count > 0 || health.warning_count > 0;

            return (
              <div
                key={group}
                className={cn(
                  'col-span-1 rounded-lg border p-4 flex flex-col justify-between',
                  'bg-white dark:bg-dark-surface',
                  health.status === 'critical'
                    ? 'border-red-200 dark:border-red-800/40'
                    : health.status === 'warning'
                    ? 'border-amber-200 dark:border-amber-800/40'
                    : 'border-gray-200 dark:border-dark-border',
                )}
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className={cn(
                      'p-1.5 rounded-md',
                      health.status === 'healthy'
                        ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400'
                        : health.status === 'warning'
                        ? 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400'
                        : 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400',
                    )}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className={cn(
                      'text-xl font-bold tabular-nums',
                      SCORE_COLOR(health.score),
                    )}>
                      {health.score}
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-0.5">
                    {meta.label}
                  </p>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 leading-snug">
                    {meta.description}
                  </p>
                </div>
                <div className="mt-3 space-y-2">
                  <ScoreBar score={health.score} />
                  <div className="flex items-center gap-2 text-[11px]">
                    {hasIssues ? (
                      <>
                        {health.critical_count > 0 && (
                          <span className="flex items-center gap-1 text-red-600 dark:text-red-400 font-medium">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                            {health.critical_count} critical
                          </span>
                        )}
                        {health.warning_count > 0 && (
                          <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            {health.warning_count} warning{health.warning_count > 1 ? 's' : ''}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                        <SignalIcon className="w-3 h-3" />
                        Nominal
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          },
        )}
      </div>
    </div>
  );
}

// ── Overview Tab ─────────────────────────────────────────────────────

function OverviewTab({ summary }: { summary?: MonitorSummary | null }) {
  const { data: alertData, isLoading: alertsLoading } = useMonitorAlerts();

  return (
    <div className="grid grid-cols-3 gap-6 mt-4">
      {/* Alert Timeline (2 cols) */}
      <div className="col-span-2">
        <Panel>
          <PanelHeader>
            <PanelTitle>Active Alerts</PanelTitle>
          </PanelHeader>
          <PanelBody>
            {alertsLoading ? (
              <div className="flex justify-center py-8"><Spinner /></div>
            ) : !alertData?.items?.length ? (
              <div className="text-center py-12 text-gray-400 dark:text-gray-500">
                <p className="text-lg font-medium">No active alerts</p>
                <p className="text-sm mt-1">All monitors are within acceptable thresholds</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {alertData.items.map((alert) => (
                  <AlertRow key={alert.id} alert={alert} />
                ))}
              </div>
            )}
          </PanelBody>
        </Panel>
      </div>

      {/* Side panels */}
      <div className="col-span-1 space-y-6">
        <RunsPanel />
        <QuickStatsPanel summary={summary} alertCount={alertData?.total ?? 0} />
      </div>
    </div>
  );
}

// ── Alert Row Component ──────────────────────────────────────────────

function AlertRow({ alert }: { alert: MonitorAlert }) {
  const updateAlert = useUpdateAlert();
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={cn(
        'border rounded-lg p-4 transition-colors',
        SEVERITY_STYLES[alert.severity],
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <span className={cn('inline-block w-2.5 h-2.5 rounded-full mt-1.5 shrink-0', SEVERITY_DOT[alert.severity])} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-[10px]">
                {GROUP_META[alert.monitor_group as MonitorGroup]?.label ?? alert.monitor_group.replace(/_/g, ' ')}
              </Badge>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(alert.triggered_at).toLocaleString()}
              </span>
            </div>
            <p className="font-medium text-sm">{alert.title}</p>
            {alert.description && (
              <p className="text-xs mt-1 opacity-80 line-clamp-2">{alert.description}</p>
            )}

            {expanded && (
              <div className="mt-3 space-y-2 text-xs">
                {alert.primary_driver && (
                  <div>
                    <span className="font-medium">Primary driver:</span> {alert.primary_driver}
                  </div>
                )}
                {alert.business_impact && (
                  <div>
                    <span className="font-medium">Business impact:</span> {alert.business_impact}
                  </div>
                )}
                {alert.observed_value != null && alert.threshold_value != null && (
                  <div>
                    <span className="font-medium">Observed:</span> {alert.observed_value.toFixed(1)} (threshold: {alert.threshold_value.toFixed(1)})
                  </div>
                )}
                {alert.recommended_action && (
                  <div className="mt-2 p-2 bg-white/50 dark:bg-black/20 rounded border border-current/10">
                    <p className="font-medium mb-1">Recommended Remediation</p>
                    <p>{alert.recommended_action.description}</p>
                    {alert.recommended_action.current_value != null && (
                      <p className="mt-1">
                        Change: {alert.recommended_action.current_value} → {alert.recommended_action.suggested_value}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Less' : 'More'}
          </Button>
          {alert.status === 'active' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => updateAlert.mutate({ alertId: alert.id, body: { status: 'reviewed' } })}
              disabled={updateAlert.isPending}
            >
              Acknowledge
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Runs Panel ───────────────────────────────────────────────────────

function RunsPanel() {
  const { data: runs, isLoading } = useMonitorRuns();
  const triggerRun = useTriggerMonitorRun();

  return (
    <Panel>
      <PanelHeader className="flex items-center justify-between">
        <PanelTitle>Recent Runs</PanelTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={() => triggerRun.mutate({ agent_identifier: 'all' })}
          disabled={triggerRun.isPending}
        >
          {triggerRun.isPending ? 'Running…' : 'Run All'}
        </Button>
      </PanelHeader>
      <PanelBody>
        {isLoading ? (
          <Spinner size="sm" />
        ) : !runs?.length ? (
          <p className="text-xs text-gray-400 py-4 text-center">No runs yet</p>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {runs.slice(0, 10).map((run) => (
              <div key={run.id} className="flex items-center justify-between text-xs border-b border-gray-100 dark:border-dark-border pb-2">
                <div>
                  <p className="font-medium text-gray-700 dark:text-gray-300">
                    {run.agent_identifier.replace('cashflow_', '').replace(/_/g, ' ')}
                  </p>
                  <p className="text-gray-400">
                    {new Date(run.started_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {run.alerts_created > 0 && (
                    <Badge variant="danger" className="text-[10px]">{run.alerts_created} alerts</Badge>
                  )}
                  <span className={cn(
                    'px-1.5 py-0.5 rounded text-[10px] font-medium',
                    run.status === 'completed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' :
                    run.status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
                  )}>
                    {run.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </PanelBody>
    </Panel>
  );
}

// ── Quick Stats Panel ────────────────────────────────────────────────

function QuickStatsPanel({ summary, alertCount }: { summary?: MonitorSummary | null; alertCount: number }) {
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Quick Stats</PanelTitle>
      </PanelHeader>
      <PanelBody>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Active Alerts</span>
            <span className="font-medium">{alertCount}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Critical</span>
            <span className="font-medium text-red-600 dark:text-red-400">{summary?.total_critical ?? 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Warnings</span>
            <span className="font-medium text-amber-600 dark:text-amber-400">{summary?.total_warning ?? 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Overall Score</span>
            <span className={cn('font-medium', SCORE_COLOR(summary?.overall_score ?? 100))}>
              {summary?.overall_score ?? 100}/100
            </span>
          </div>
        </div>
      </PanelBody>
    </Panel>
  );
}

// ── Forecast Accuracy Tab ────────────────────────────────────────────

function ForecastAccuracyTab() {
  const { data: accuracyData, isLoading } = useAccuracyData();
  const { data: alertData } = useMonitorAlerts('forecast_accuracy');

  return (
    <div className="space-y-6 mt-4">
      {alertData?.items && alertData.items.length > 0 && (
        <Alert variant="error">
          {alertData.items.length} accuracy alert{alertData.items.length > 1 ? 's' : ''} active.
          {' '}Highest: {alertData.items[0]?.title}
        </Alert>
      )}

      <Panel>
        <PanelHeader>
          <PanelTitle>Rolling MAPE by Line Item</PanelTitle>
        </PanelHeader>
        <PanelBody>
          {isLoading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : !accuracyData?.trend?.length ? (
            <p className="text-sm text-gray-400 text-center py-8">No accuracy data available</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-dark-border text-left">
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Line Item</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400 text-right">Avg MAPE</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400 text-right">Latest Rolling 8W</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400 text-right">Weeks</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {accuracyData.trend.map((row) => {
                    const mape = row.latest_rolling_mape ?? row.avg_mape;
                    const status = mape > 20 ? 'critical' : mape > 12 ? 'warning' : 'healthy';
                    return (
                      <tr key={row.line_item} className="border-b border-gray-100 dark:border-dark-border/50">
                        <td className="py-2.5 font-medium">{row.line_item.replace(/_/g, ' ')}</td>
                        <td className="py-2.5 text-right">{row.avg_mape.toFixed(1)}%</td>
                        <td className="py-2.5 text-right">{mape.toFixed(1)}%</td>
                        <td className="py-2.5 text-right">{row.weeks_measured}</td>
                        <td className="py-2.5">
                          <span className={cn(
                            'inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
                            STATUS_COLOR(status),
                          )}>
                            {status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </PanelBody>
      </Panel>

      {/* Alert details for this group */}
      {alertData?.items && alertData.items.length > 0 && (
        <Panel>
          <PanelHeader>
            <PanelTitle>Accuracy Alerts</PanelTitle>
          </PanelHeader>
          <PanelBody>
            <div className="space-y-3">
              {alertData.items.map((alert) => (
                <AlertRow key={alert.id} alert={alert} />
              ))}
            </div>
          </PanelBody>
        </Panel>
      )}
    </div>
  );
}

// ── Model Drift Tab ──────────────────────────────────────────────────

function ModelDriftTab() {
  const { data: driftData, isLoading } = useDriftSummary();
  const { data: alertData } = useMonitorAlerts('model_drift');

  return (
    <div className="space-y-6 mt-4">
      {alertData?.items && alertData.items.length > 0 && (
        <Alert variant="error">
          {alertData.items.length} drift alert{alertData.items.length > 1 ? 's' : ''} detected.
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-6">
        {/* Behavior Class Drift */}
        <Panel>
          <PanelHeader>
            <PanelTitle>AR Behavior Class Timing</PanelTitle>
          </PanelHeader>
          <PanelBody>
            {isLoading ? (
              <Spinner />
            ) : !driftData?.drifting_classes?.length ? (
              <p className="text-sm text-gray-400 text-center py-6">No behavior class drift detected</p>
            ) : (
              <div className="space-y-3">
                {driftData.drifting_classes.map((row: any) => (
                  <div key={row.behavior_class} className="p-3 border rounded-lg border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{row.behavior_class}</span>
                      <Badge variant="outline">{row.drift_weeks}/{row.total_weeks} weeks drifting</Badge>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Modeled delay: {row.modeled_delay}d — Actual: {Number(row.latest_actual_delay).toFixed(1)}d — Deviation: +{Number(row.avg_deviation).toFixed(1)}d
                    </p>
                  </div>
                ))}
              </div>
            )}
          </PanelBody>
        </Panel>

        {/* Bucket Recovery Drift */}
        <Panel>
          <PanelHeader>
            <PanelTitle>Aging Bucket Recovery Rates</PanelTitle>
          </PanelHeader>
          <PanelBody>
            {isLoading ? (
              <Spinner />
            ) : !driftData?.bucket_drift?.length ? (
              <p className="text-sm text-gray-400 text-center py-6">No bucket recovery drift detected</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-dark-border text-left">
                      <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Bucket</th>
                      <th className="pb-2 font-medium text-gray-500 dark:text-gray-400 text-right">Modeled</th>
                      <th className="pb-2 font-medium text-gray-500 dark:text-gray-400 text-right">Actual</th>
                      <th className="pb-2 font-medium text-gray-500 dark:text-gray-400 text-right">Deviation</th>
                      <th className="pb-2 font-medium text-gray-500 dark:text-gray-400 text-right">Breaches</th>
                    </tr>
                  </thead>
                  <tbody>
                    {driftData.bucket_drift.map((row: any) => (
                      <tr key={row.aging_bucket} className="border-b border-gray-100 dark:border-dark-border/50">
                        <td className="py-2 font-medium">{row.aging_bucket}</td>
                        <td className="py-2 text-right">{(Number(row.avg_modeled_rate) * 100).toFixed(0)}%</td>
                        <td className="py-2 text-right">{(Number(row.avg_actual_rate) * 100).toFixed(0)}%</td>
                        <td className={cn(
                          'py-2 text-right font-medium',
                          Number(row.avg_deviation_pct) < -5 ? 'text-red-600 dark:text-red-400' : '',
                        )}>
                          {Number(row.avg_deviation_pct).toFixed(1)}%
                        </td>
                        <td className="py-2 text-right">{row.breach_weeks}/{row.total_weeks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </PanelBody>
        </Panel>
      </div>

      {/* Drift Alerts */}
      {alertData?.items && alertData.items.length > 0 && (
        <Panel>
          <PanelHeader>
            <PanelTitle>Drift Alerts</PanelTitle>
          </PanelHeader>
          <PanelBody>
            <div className="space-y-3">
              {alertData.items.map((alert) => (
                <AlertRow key={alert.id} alert={alert} />
              ))}
            </div>
          </PanelBody>
        </Panel>
      )}
    </div>
  );
}

// ── Data Quality Tab ─────────────────────────────────────────────────

function DataQualityTab() {
  const { data: dqStatus, isLoading } = useDataQualityStatus();
  const { data: alertData } = useMonitorAlerts('data_quality');

  const freshness = (dqStatus as any)?.freshness ?? [];
  const dqAlerts = (dqStatus as any)?.alerts ?? [];

  return (
    <div className="space-y-6 mt-4">
      {dqAlerts.length > 0 && (
        <Alert variant="warning">
          {dqAlerts.length} data source{dqAlerts.length > 1 ? 's' : ''} need{dqAlerts.length === 1 ? 's' : ''} attention.
        </Alert>
      )}

      <Panel>
        <PanelHeader>
          <PanelTitle>Data Feed Freshness</PanelTitle>
        </PanelHeader>
        <PanelBody>
          {isLoading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : !freshness.length ? (
            <p className="text-sm text-gray-400 text-center py-8">No data load information available</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-dark-border text-left">
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Feed</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Last Load</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400 text-right">Days Ago</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400 text-right">Records</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {freshness.map((row: any) => {
                    const days = row.days_since_load ?? 0;
                    const status = days > 3 ? 'critical' : days > 1 ? 'warning' : 'healthy';
                    return (
                      <tr key={row.load_type} className="border-b border-gray-100 dark:border-dark-border/50">
                        <td className="py-2.5 font-medium">{(row.load_type ?? '').replace(/_/g, ' ')}</td>
                        <td className="py-2.5 text-gray-500 dark:text-gray-400">{row.latest_load_date ?? '—'}</td>
                        <td className="py-2.5 text-right">{days}</td>
                        <td className="py-2.5 text-right">{row.latest_record_count?.toLocaleString() ?? '—'}</td>
                        <td className="py-2.5">
                          <span className={cn(
                            'inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
                            STATUS_COLOR(status),
                          )}>
                            {status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </PanelBody>
      </Panel>

      {alertData?.items && alertData.items.length > 0 && (
        <Panel>
          <PanelHeader><PanelTitle>Data Quality Alerts</PanelTitle></PanelHeader>
          <PanelBody>
            <div className="space-y-3">
              {alertData.items.map((alert) => (
                <AlertRow key={alert.id} alert={alert} />
              ))}
            </div>
          </PanelBody>
        </Panel>
      )}
    </div>
  );
}

// ── Agent Health Tab ─────────────────────────────────────────────────

function AgentHealthTab() {
  const { data: runs, isLoading } = useMonitorRuns();
  const triggerRun = useTriggerMonitorRun();

  const stats = useMemo(() => {
    if (!runs?.length) return null;
    const total = runs.length;
    const completed = runs.filter((r) => r.status === 'completed').length;
    const failed = runs.filter((r) => r.status === 'failed').length;
    const avgDuration = runs.filter((r) => r.duration_ms).reduce((s, r) => s + (r.duration_ms ?? 0), 0) / (total || 1);
    const totalAlerts = runs.reduce((s, r) => s + (r.alerts_created ?? 0), 0);
    return { total, completed, failed, avgDuration: Math.round(avgDuration), totalAlerts };
  }, [runs]);

  return (
    <div className="space-y-6 mt-4">
      {stats && (
        <MetricCardGroup columns={4}>
          <MetricCard label="Total Runs" value={stats.total} />
          <MetricCard
            label="Success Rate"
            value={`${((stats.completed / stats.total) * 100).toFixed(0)}%`}
            trend={stats.failed > 0 ? { direction: 'down', value: `${stats.failed} failed`, favorable: false } : undefined}
          />
          <MetricCard label="Avg Duration" value={`${(stats.avgDuration / 1000).toFixed(1)}s`} />
          <MetricCard label="Alerts Created" value={stats.totalAlerts} />
        </MetricCardGroup>
      )}

      <Panel>
        <PanelHeader className="flex items-center justify-between">
          <PanelTitle>Agent Run History</PanelTitle>
          <div className="flex gap-2">
            {['cashflow_forecast_accuracy_monitor', 'cashflow_model_drift_monitor', 'cashflow_data_quality_monitor'].map((agent) => (
              <Button
                key={agent}
                size="sm"
                variant="outline"
                onClick={() => triggerRun.mutate({ agent_identifier: agent })}
                disabled={triggerRun.isPending}
              >
                Run {agent.replace('cashflow_', '').replace('_monitor', '').replace(/_/g, ' ')}
              </Button>
            ))}
          </div>
        </PanelHeader>
        <PanelBody>
          {isLoading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : !runs?.length ? (
            <p className="text-sm text-gray-400 text-center py-8">No runs recorded yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-dark-border text-left">
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Agent</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Started</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400 text-right">Duration</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400 text-right">Alerts</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Status</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <tr key={run.id} className="border-b border-gray-100 dark:border-dark-border/50">
                      <td className="py-2 font-medium">
                        {run.agent_identifier.replace('cashflow_', '').replace(/_/g, ' ')}
                      </td>
                      <td className="py-2 text-gray-500 dark:text-gray-400">
                        {new Date(run.started_at).toLocaleString()}
                      </td>
                      <td className="py-2 text-right">
                        {run.duration_ms ? `${(run.duration_ms / 1000).toFixed(1)}s` : '—'}
                      </td>
                      <td className="py-2 text-right">{run.alerts_created}</td>
                      <td className="py-2">
                        <span className={cn(
                          'inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
                          run.status === 'completed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' :
                          run.status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                          'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
                        )}>
                          {run.status}
                        </span>
                      </td>
                      <td className="py-2 text-xs text-red-500 truncate max-w-[200px]">
                        {run.error_message ?? ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </PanelBody>
      </Panel>
    </div>
  );
}
